// @ts-nocheck
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2, GraduationCap, Plus, Minus, Users } from 'lucide-react';
import { z } from 'zod';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface NcaaLiveCoachModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventName: string;
  pricing: {
    d1FirstCoachFee: number;
    d1AdditionalCoachFee: number;
    otherFirstCoachFee: number;
    otherAdditionalCoachFee: number;
  };
  hospitalityDays: string[];
  /** Legacy single coupon stored on the event (supports older events). */
  legacyCoupon?: {
    code: string;
    discount_type: 'percentage' | 'fixed';
    discount_value: number;
  };
  /** Instructions to display at the top of the form */
  instructions?: string;
  onSuccess?: () => void;
}

interface EventCoupon {
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
}

interface AdditionalCoach {
  firstName: string;
  lastName: string;
}

const NcaaLiveCoachModal: React.FC<NcaaLiveCoachModalProps> = ({
  open,
  onOpenChange,
  eventId,
  eventName,
  pricing,
  hospitalityDays = [],
  legacyCoupon,
  instructions,
  onSuccess
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [division, setDivision] = useState<'D1' | 'D2' | 'D3' | 'JUCO' | 'NAIA'>('D1');
  const [additionalCoachQty, setAdditionalCoachQty] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [couponValid, setCouponValid] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [eventCoupons, setEventCoupons] = useState<EventCoupon[]>([]);
  const [appliedCouponData, setAppliedCouponData] = useState<EventCoupon | null>(null);

  const allCoupons = useMemo(() => {
    const map = new Map<string, EventCoupon>();

    for (const c of eventCoupons) {
      map.set(c.code.toUpperCase(), { ...c, code: c.code.toUpperCase() });
    }

    if (legacyCoupon?.code?.trim()) {
      const code = legacyCoupon.code.trim().toUpperCase();
      map.set(code, {
        code,
        discount_type: legacyCoupon.discount_type,
        discount_value: Number(legacyCoupon.discount_value) || 0,
      });
    }

    const arr = Array.from(map.values());
    console.log('🎫 NcaaLiveCoachModal allCoupons:', arr, 'legacyCoupon:', legacyCoupon, 'eventCoupons:', eventCoupons);
    return arr;
  }, [eventCoupons, legacyCoupon]);
  const [formData, setFormData] = useState({
    // Person filling form
    fillerFirstName: '',
    fillerLastName: '',
    fillerEmail: '',
    schoolAffiliation: '',
    // Primary coach
    coachFirstName: '',
    coachLastName: '',
    coachEmail: '',
    coachPhone: '',
  });

  const [additionalCoaches, setAdditionalCoaches] = useState<AdditionalCoach[]>([
    { firstName: '', lastName: '' },
    { firstName: '', lastName: '' },
    { firstName: '', lastName: '' }
  ]);

  // Fetch coupon codes for this event
  useEffect(() => {
    const fetchCoupons = async () => {
      const { data, error } = await supabase
        .from('event_coupon_codes')
        .select('code, discount_type, discount_value')
        .eq('event_id', eventId)
        .eq('is_active', true);

      if (!error && data) {
        setEventCoupons(
          data
            .filter(c => (c.code || '').trim().length > 0)
            .map(c => ({
              code: String(c.code).toUpperCase().trim(),
              discount_type: c.discount_type as 'percentage' | 'fixed',
              discount_value: Number(c.discount_value),
            }))
        );
      }
    };

    if (open && eventId) {
      // reset coupon state on open
      setAppliedCoupon('');
      setCouponInput('');
      setCouponValid(false);
      setAppliedCouponData(null);

      fetchCoupons();
    }
  }, [open, eventId]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAdditionalCoachChange = (index: number, field: keyof AdditionalCoach, value: string) => {
    const updated = [...additionalCoaches];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalCoaches(updated);
  };

  const toggleDay = (day: string) => {
    setSelectedDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const couponSchema = useMemo(
    () =>
      z
        .string()
        .trim()
        .min(1, 'Enter a coupon code')
        .max(32, 'Coupon code is too long')
        .regex(/^[A-Z0-9-_]+$/, 'Coupon code contains invalid characters'),
    []
  );

  const applyCoupon = () => {
    const parsed = couponSchema.safeParse(couponInput.toUpperCase());
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || 'Invalid coupon code');
      return;
    }

    const enteredCode = parsed.data;
    const matchedCoupon = allCoupons.find(c => c.code === enteredCode);

    if (!matchedCoupon) {
      toast.error('Invalid coupon code');
      setCouponValid(false);
      setAppliedCouponData(null);
      return;
    }

    setAppliedCoupon(enteredCode);
    setCouponValid(true);
    setAppliedCouponData(matchedCoupon);

    const displayValue = matchedCoupon.discount_type === 'percentage'
      ? Math.min(matchedCoupon.discount_value, 100)
      : matchedCoupon.discount_value / 100; // cents -> dollars

    if (matchedCoupon.discount_type === 'percentage' && matchedCoupon.discount_value >= 100) {
      toast.success('Coupon applied - registration is now free!');
    } else if (matchedCoupon.discount_type === 'percentage') {
      toast.success(`Coupon applied - ${displayValue}% off!`);
    } else {
      toast.success(`Coupon applied - $${displayValue.toFixed(2)} off!`);
    }
  };

  // Calculate pricing - D1 vs all others (D2, D3, JUCO, NAIA)
  const isD1 = division === 'D1';
  const firstCoachPrice = isD1 ? pricing.d1FirstCoachFee : pricing.otherFirstCoachFee;
  const additionalCoachPrice = isD1 ? pricing.d1AdditionalCoachFee : pricing.otherAdditionalCoachFee;
  const subtotal = firstCoachPrice + (additionalCoachPrice * additionalCoachQty);
  
  // Get discount values from applied coupon
  const couponDiscountType = appliedCouponData?.discount_type || 'percentage';
  const couponDiscountValue = appliedCouponData?.discount_value || 0;
  
  // For percentage, couponDiscountValue is the actual percentage (e.g., 95 for 95%)
  // For fixed, couponDiscountValue is in cents
  const normalizedDiscountValue = couponDiscountType === 'percentage' 
    ? Math.min(couponDiscountValue, 100) 
    : couponDiscountValue;

  // Calculate discount amount
  const calculateDiscount = () => {
    if (!couponValid || !appliedCouponData) return 0;
    if (couponDiscountType === 'percentage') {
      // Use normalized value (actual percentage), calculate discount, cap at subtotal
      const discountPercent = Math.min(normalizedDiscountValue, 100);
      return Math.min(Math.round(subtotal * (discountPercent / 100)), subtotal);
    } else {
      // Fixed amount discount (stored in cents) - cap at subtotal
      return Math.min(normalizedDiscountValue, subtotal);
    }
  };
  
  const discountAmount = calculateDiscount();
  // Ensure discounted subtotal never goes negative
  const discountedSubtotal = Math.max(0, subtotal - discountAmount);
  const processingFee = discountedSubtotal > 0 ? Math.round(discountedSubtotal * 0.029 + 30) : 0; // 2.9% + $0.30
  // Ensure total never goes negative
  const total = Math.max(0, discountedSubtotal + processingFee);

  const validateForm = (): boolean => {
    if (!formData.fillerFirstName || !formData.fillerLastName) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.fillerEmail) {
      toast.error('Please enter your email');
      return false;
    }
    if (!formData.schoolAffiliation) {
      toast.error('Please enter your school affiliation');
      return false;
    }
    if (!formData.coachFirstName || !formData.coachLastName) {
      toast.error('Please enter the primary coach name');
      return false;
    }
    if (!formData.coachEmail) {
      toast.error('Please enter the coach email');
      return false;
    }
    if (!formData.coachPhone) {
      toast.error('Please enter the coach phone');
      return false;
    }
    // Validate phone format XXX-XXX-XXXX (12 characters with dashes)
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    if (!phoneRegex.test(formData.coachPhone)) {
      toast.error('Please enter a valid phone number (XXX-XXX-XXXX)');
      return false;
    }
    // Require at least one hospitality day if hospitality days are configured
    if (hospitalityDays.length > 0 && selectedDays.length === 0) {
      toast.error('Please select at least one hospitality attendance day');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Build additional coaches list
      const additionalCoachList = additionalCoaches
        .slice(0, additionalCoachQty)
        .filter(c => c.firstName || c.lastName)
        .map(c => `${c.firstName} ${c.lastName}`.trim());

      // Create event coach record with full pricing metadata
      const { data: coachRecord, error: coachError } = await supabase
        .from('event_coaches')
        .insert({
          event_id: eventId,
          coach_name: `${formData.coachFirstName} ${formData.coachLastName}`,
          college_name: formData.schoolAffiliation,
          division: division,
          coach_email: formData.coachEmail,
          coach_phone: formData.coachPhone,
          payment_status: total > 0 ? 'pending' : 'free',
          hide_from_public: true, // Hidden by default - admin can show later
          metadata: {
            filler_name: `${formData.fillerFirstName} ${formData.fillerLastName}`,
            filler_email: formData.fillerEmail,
            additional_coaches: additionalCoachList,
            additional_coach_qty: additionalCoachQty,
            hospitality_days: selectedDays,
            is_ncaa_live: true,
            division: division,
            coupon_applied: couponValid ? appliedCoupon : null,
            discount_amount: discountAmount,
            // Pricing fields for merge tags
            registration_amount: firstCoachPrice,
            extras_amount: additionalCoachPrice * additionalCoachQty,
            processing_fee: processingFee,
            total_amount: total,
            amount_paid: 0,
            amount_owed: total,
            team_name: formData.schoolAffiliation
          }
        })
        .select()
        .single();

      if (coachError) {
        console.error('Event coach insert error:', coachError);
        throw coachError;
      }
      
      if (!coachRecord) {
        console.error('Event coach insert failed: no record returned');
        throw new Error('Failed to create registration record');
      }
      
      console.log('Event coach created successfully:', coachRecord.id);

      if (total > 0) {
        // Create Stripe checkout
        const { data: checkoutData, error: checkoutError } = await invokeEdgeFunction('create-coach-attendance-checkout', {
          body: {
            eventId,
            eventCoachId: coachRecord.id,
            amount: total,
            coachEmail: formData.coachEmail,
            coachName: `${formData.coachFirstName} ${formData.coachLastName}`,
            collegeName: formData.schoolAffiliation,
            metadata: {
              is_ncaa_live: true,
              division: division,
              first_coach_fee: firstCoachPrice,
              additional_coaches: additionalCoachQty,
              additional_coach_fee: additionalCoachPrice * additionalCoachQty
            }
          }
        });

        if (checkoutError || !checkoutData?.url) {
          throw new Error('Failed to create payment session');
        }

        window.location.href = checkoutData.url;
        return;
      }

      // Free registration - send notification
      await invokeEdgeFunction('coach-attendance-notification', {
        body: {
          eventId,
          eventCoachId: coachRecord.id,
          coachName: `${formData.coachFirstName} ${formData.coachLastName}`,
          coachEmail: formData.coachEmail,
          coachPhone: formData.coachPhone,
          collegeName: formData.schoolAffiliation,
          division: division,
          paymentStatus: 'free',
          metadata: {
            is_ncaa_live: true,
            additional_coaches: additionalCoachList,
            hospitality_days: selectedDays,
            filler_name: `${formData.fillerFirstName} ${formData.fillerLastName}`,
            filler_email: formData.fillerEmail,
            coach_phone: formData.coachPhone
          }
        }
      });

      toast.success('Successfully registered for the event!');
      onSuccess?.();
      onOpenChange(false);
      // Redirect to confirmation page
      navigate(`/coach-registration-confirmation?id=${coachRecord.id}&event_id=${eventId}`);
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <GraduationCap className="h-6 w-6" />
            College Coach Registration
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{eventName}</p>
        </DialogHeader>

        {/* Instructions Section - Always show for NCAA Live events */}
        <Card className="bg-muted/50 border-primary/20">
          <CardContent className="pt-4">
            <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap">
              {instructions || `Coaches-

Thank you for your interest in attending RecruitLook Hoops' NCAA Live Period in CHICAGO on July 16-19 weekend. Here are a few things to know.

1.) The first attending coach from a school must purchase a packet. Each additional coach can purchase weekend passes that include a packet.

2.) College coaches must check in and pick up packets at Romeoville Athletics and Event Center on THURSDAY & FRIDAY. And Supreme Courts on SATURDAY & SUNDAY. These are the only location where packets will be.

3.) We will have a hospitality room at Romeoville Athletic Event Center on Thursday and Friday only, and at Supreme Courts on Saturday & Sunday only. We will ask in the form below to let us know the coach(es) estimated dates of attending to help us prepare food.`}
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Person Filling Form */}
          <Card>
            <CardContent className="pt-4 space-y-4">
              <h3 className="font-semibold">Person filling out this form *</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={formData.fillerFirstName}
                    onChange={(e) => handleInputChange('fillerFirstName', e.target.value)}
                    placeholder="First Name"
                    required
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={formData.fillerLastName}
                    onChange={(e) => handleInputChange('fillerLastName', e.target.value)}
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Your Email *</Label>
                <Input
                  type="email"
                  value={formData.fillerEmail}
                  onChange={(e) => handleInputChange('fillerEmail', e.target.value)}
                  placeholder="your.email@school.edu"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">Receipt will be sent to this email</p>
              </div>

              <div>
                <Label>School Affiliation *</Label>
                <Input
                  value={formData.schoolAffiliation}
                  onChange={(e) => handleInputChange('schoolAffiliation', e.target.value)}
                  placeholder="What college or university are you at?"
                  required
                />
              </div>

              <div>
                <Label>What Division Level *</Label>
                <Select 
                  value={division} 
                  onValueChange={(v) => setDivision(v as 'D1' | 'D2' | 'D3' | 'JUCO' | 'NAIA')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="D1">D1</SelectItem>
                    <SelectItem value="D2">D2</SelectItem>
                    <SelectItem value="D3">D3</SelectItem>
                    <SelectItem value="JUCO">JUCO</SelectItem>
                    <SelectItem value="NAIA">NAIA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Primary Coach */}
          <Card>
            <CardContent className="pt-4 space-y-4">
              <h3 className="font-semibold">Coach's Details *</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={formData.coachFirstName}
                    onChange={(e) => handleInputChange('coachFirstName', e.target.value)}
                    placeholder="First Name"
                    required
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={formData.coachLastName}
                    onChange={(e) => handleInputChange('coachLastName', e.target.value)}
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Coach's Email *</Label>
                <Input
                  type="email"
                  value={formData.coachEmail}
                  onChange={(e) => handleInputChange('coachEmail', e.target.value)}
                  placeholder="coach@university.edu"
                  required
                />
              </div>
              <div>
                <Label>Coach's Cell *</Label>
                <Input
                  type="tel"
                  formatPhone={true}
                  value={formData.coachPhone}
                  onChange={(e) => handleInputChange('coachPhone', e.target.value)}
                  placeholder="xxx-xxx-xxxx"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Coaches */}
          <Card>
            <CardContent className="pt-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                Additional Coaches Attending
              </h3>
              <p className="text-sm text-muted-foreground">
                Bringing more coaches? Add them here.
              </p>
              
              {[0, 1, 2].map((index) => (
                <div key={index} className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Additional Coach {index + 1} - First Name</Label>
                    <Input
                      value={additionalCoaches[index]?.firstName || ''}
                      onChange={(e) => handleAdditionalCoachChange(index, 'firstName', e.target.value)}
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={additionalCoaches[index]?.lastName || ''}
                      onChange={(e) => handleAdditionalCoachChange(index, 'lastName', e.target.value)}
                      placeholder="Last Name"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Hospitality Days */}
          {hospitalityDays.length > 0 && (
            <Card>
              <CardContent className="pt-4 space-y-4">
                <h3 className="font-semibold">
                  Coach Hospitality Room Attendance <span className="text-destructive">*</span>
                </h3>
                <p className="text-sm text-muted-foreground">
                  Please check all days the 1st coach will be in attendance (required)
                </p>
                <div className="space-y-2">
                  {hospitalityDays.map((day) => (
                    <div key={day} className="flex items-center space-x-2">
                      <Checkbox
                        id={`day-${day}`}
                        checked={selectedDays.includes(day)}
                        onCheckedChange={() => toggleDay(day)}
                      />
                      <Label htmlFor={`day-${day}`} className="font-normal cursor-pointer">
                        {day}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pricing Section */}
          <Card className="border-primary/20">
            <CardContent className="pt-4 space-y-4">
              <h3 className="font-semibold text-lg">Registration Options</h3>
              
              {/* First Coach */}
              <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">
                    {division} - 1st Coach Registration
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isD1 ? 'Division I pricing' : 'All other divisions pricing'}
                  </p>
                </div>
                <span className="font-bold text-lg">${(firstCoachPrice / 100).toFixed(2)}</span>
              </div>

              {/* Additional Coaches */}
              <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">
                    {division} - Additional Coaches
                  </p>
                  <p className="text-sm text-muted-foreground">
                    ${(additionalCoachPrice / 100).toFixed(2)} per additional coach
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setAdditionalCoachQty(Math.max(0, additionalCoachQty - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-8 text-center font-bold">{additionalCoachQty}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setAdditionalCoachQty(Math.min(3, additionalCoachQty + 1))}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Coupon Code - show if any coupon is configured (table-based or legacy) */}
              {allCoupons.length > 0 && (
                <div className="space-y-2">
                  <Label>Coupon Code</Label>
                  <div className="flex gap-2">
                    <Input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      disabled={couponValid}
                      maxLength={32}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={applyCoupon}
                      disabled={couponValid || !couponInput.trim()}
                    >
                      {couponValid ? '✓ Applied' : 'Apply'}
                    </Button>
                  </div>
                </div>
              )}

              <Separator />

              {/* Totals */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${(subtotal / 100).toFixed(2)}</span>
                </div>
                {couponValid && discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Coupon Discount ({couponDiscountType === 'percentage' ? `${normalizedDiscountValue}%` : `$${(normalizedDiscountValue / 100).toFixed(2)}`})</span>
                    <span>-${(discountAmount / 100).toFixed(2)}</span>
                  </div>
                )}
                {total > 0 && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Processing Fee</span>
                    <span>${(processingFee / 100).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>${(total / 100).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : total > 0 ? (
              `Register & Pay $${(total / 100).toFixed(2)}`
            ) : (
              'Complete Free Registration'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NcaaLiveCoachModal;